
export type JobType = 'Executive Search' | 'Full-time' | 'Board' | 'Interim' | 'Advisory'
export type Discipline =
  | 'Executive Leadership'
  | 'Finance'
  | 'Technology'
  | 'Marketing'
  | 'Operations'
  | 'People & Culture'
  | 'Legal'

export interface Job {
  id: string
  title: string
  company: string
  companyBlurb: string
  location: string
  remote: 'On-site' | 'Hybrid' | 'Remote'
  type: JobType
  discipline: Discipline
  seniority: 'Director' | 'VP' | 'C-Suite' | 'Board'
  compensation: string
  postedDays: number
  confidential: boolean
  summary: string
  responsibilities: string[]
  requirements: string[]
  featured?: boolean
}

export const jobs: Job[] = [
  {
    id: 'ceo-lumen',
    title: 'Chief Executive Officer',
    company: 'Lumen Renewables',
    companyBlurb: 'A €2.4B clean-energy group operating across 14 European markets.',
    location: 'London, United Kingdom',
    remote: 'Hybrid',
    type: 'Executive Search',
    discipline: 'Executive Leadership',
    seniority: 'C-Suite',
    compensation: '£450k–£620k + equity',
    postedDays: 2,
    confidential: true,
    featured: true,
    summary:
      'Lead the next chapter of one of Europe\u2019s fastest-scaling renewable energy platforms, driving strategy, capital allocation and international expansion.',
    responsibilities: [
      'Set and execute the group\u2019s five-year growth strategy across generation and storage',
      'Partner with the Board and investors on capital allocation and M&A',
      'Build and mentor a world-class executive leadership team',
      'Represent the group with regulators, media and institutional stakeholders',
    ],
    requirements: [
      '15+ years of senior leadership within energy, infrastructure or industrials',
      'Demonstrated success scaling a business through a growth or transformation phase',
      'Board-level gravitas and fluency with institutional investors',
      'International, multi-market operating experience',
    ],
  },
  {
    id: 'cfo-northwind',
    title: 'Chief Financial Officer',
    company: 'Northwind Capital',
    companyBlurb: 'A private equity firm with $9B assets under management.',
    location: 'New York, United States',
    remote: 'On-site',
    type: 'Executive Search',
    discipline: 'Finance',
    seniority: 'C-Suite',
    compensation: '$500k–$700k + carry',
    postedDays: 5,
    confidential: false,
    featured: true,
    summary:
      'Own the financial strategy of a leading mid-market private equity platform, from fund operations to portfolio value creation.',
    responsibilities: [
      'Lead fund finance, treasury, tax and investor reporting',
      'Drive value-creation initiatives across the portfolio',
      'Strengthen controls, systems and the finance operating model',
      'Serve as a strategic partner to the Managing Partners',
    ],
    requirements: [
      'Qualified accountant with 12+ years in finance leadership',
      'Deep private equity or asset management experience',
      'Track record building high-performing finance functions',
      'Exceptional investor and stakeholder communication',
    ],
  },
  {
    id: 'cto-vantage',
    title: 'Chief Technology Officer',
    company: 'Vantage Health',
    companyBlurb: 'A Series C digital health platform serving 4M+ patients.',
    location: 'Remote (US / EU)',
    remote: 'Remote',
    type: 'Executive Search',
    discipline: 'Technology',
    seniority: 'C-Suite',
    compensation: '$400k–$540k + equity',
    postedDays: 1,
    confidential: false,
    featured: true,
    summary:
      'Define and scale the technology vision for a category-defining digital health company at the intersection of care and data.',
    responsibilities: [
      'Own product engineering, platform and data science strategy',
      'Scale the engineering organisation from 90 to 200+',
      'Champion security, privacy and clinical-grade reliability',
      'Partner with product and clinical leadership on the roadmap',
    ],
    requirements: [
      'Proven CTO or VP Engineering at a high-growth software company',
      'Experience scaling teams and platforms through hyper-growth',
      'Regulated industry exposure (health, fintech) preferred',
      'Inspiring, values-driven technical leadership',
    ],
  },
  {
    id: 'cmo-atelier',
    title: 'Chief Marketing Officer',
    company: 'Maison Atelier',
    companyBlurb: 'A global luxury lifestyle house with a 60-year heritage.',
    location: 'Paris, France',
    remote: 'Hybrid',
    type: 'Executive Search',
    discipline: 'Marketing',
    seniority: 'C-Suite',
    compensation: '\u20ac320k–\u20ac420k',
    postedDays: 8,
    confidential: true,
    summary:
      'Steward and modernise one of the world\u2019s most recognised luxury brands across markets, channels and generations.',
    responsibilities: [
      'Own global brand, communications and customer strategy',
      'Lead the digital and experiential transformation of the house',
      'Protect brand equity while driving measurable growth',
      'Build and lead a global marketing organisation',
    ],
    requirements: [
      'Senior brand leadership within luxury, fashion or premium consumer',
      'Global, multi-market marketing experience',
      'Fluency across brand storytelling and performance',
      'Refined aesthetic sensibility and commercial rigour',
    ],
  },
  {
    id: 'coo-meridian',
    title: 'Chief Operating Officer',
    company: 'Meridian Logistics',
    companyBlurb: 'A cross-border logistics network moving 1M+ shipments monthly.',
    location: 'Singapore',
    remote: 'On-site',
    type: 'Executive Search',
    discipline: 'Operations',
    seniority: 'C-Suite',
    compensation: 'S$480k–S$620k',
    postedDays: 11,
    confidential: false,
    summary:
      'Drive operational excellence and margin expansion across a complex, multi-country logistics operation.',
    responsibilities: [
      'Own end-to-end operations across the regional network',
      'Lead a large-scale operational transformation programme',
      'Improve unit economics, reliability and customer experience',
      'Build operational leadership bench strength',
    ],
    requirements: [
      'Senior operations leadership in logistics, supply chain or manufacturing',
      'Proven margin and efficiency transformation record',
      'Experience operating across Asia-Pacific markets',
      'Data-driven, systems-oriented operator',
    ],
  },
  {
    id: 'chro-arbor',
    title: 'Chief People Officer',
    company: 'Arbor Group',
    companyBlurb: 'A 12,000-person professional services firm.',
    location: 'Toronto, Canada',
    remote: 'Hybrid',
    type: 'Executive Search',
    discipline: 'People & Culture',
    seniority: 'C-Suite',
    compensation: 'C$360k–C$460k',
    postedDays: 6,
    confidential: false,
    summary:
      'Shape the culture, talent and leadership agenda of a large, values-led professional services organisation.',
    responsibilities: [
      'Own the people strategy, from talent to total rewards',
      'Lead culture, DEI and leadership development at scale',
      'Advise the CEO and Board on organisational design',
      'Modernise the HR operating model and technology',
    ],
    requirements: [
      'Senior HR / People leadership in a large, complex organisation',
      'Track record shaping culture and leadership at scale',
      'Board-level advisory experience',
      'Deep expertise across the talent lifecycle',
    ],
  },
  {
    id: 'gc-helios',
    title: 'General Counsel',
    company: 'Helios Bank',
    companyBlurb: 'A digital-first challenger bank across three continents.',
    location: 'Dubai, UAE',
    remote: 'On-site',
    type: 'Executive Search',
    discipline: 'Legal',
    seniority: 'C-Suite',
    compensation: 'Competitive + equity',
    postedDays: 14,
    confidential: true,
    summary:
      'Lead the legal, regulatory and governance function of a fast-scaling international banking group.',
    responsibilities: [
      'Own legal, compliance and regulatory strategy across markets',
      'Advise the Board on governance and risk',
      'Lead on licensing, M&A and corporate transactions',
      'Build a scalable, commercially-minded legal function',
    ],
    requirements: [
      'Senior legal leadership in banking or financial services',
      'Multi-jurisdiction regulatory experience',
      'Strong commercial and board advisory skills',
      'Experience in a high-growth, scaling environment',
    ],
  },
  {
    id: 'board-terra',
    title: 'Non-Executive Board Director',
    company: 'Terra Foods',
    companyBlurb: 'A publicly-listed sustainable food producer.',
    location: 'Amsterdam, Netherlands',
    remote: 'Hybrid',
    type: 'Board',
    discipline: 'Executive Leadership',
    seniority: 'Board',
    compensation: '\u20ac90k retainer',
    postedDays: 3,
    confidential: false,
    summary:
      'Join the Board of a listed, purpose-driven food business to guide strategy, governance and sustainability.',
    responsibilities: [
      'Provide independent oversight and strategic counsel',
      'Chair or serve on the Audit or Remuneration committee',
      'Champion sustainability and long-term value creation',
      'Support executive succession planning',
    ],
    requirements: [
      'Prior board or C-suite experience in consumer or FMCG',
      'Strong governance and financial literacy',
      'Sustainability or ESG expertise valued',
      'Independent, considered judgement',
    ],
  },
  {
    id: 'vp-eng-nimbus',
    title: 'VP of Engineering',
    company: 'Nimbus AI',
    companyBlurb: 'A frontier AI infrastructure startup, Series B.',
    location: 'San Francisco, United States',
    remote: 'Hybrid',
    type: 'Full-time',
    discipline: 'Technology',
    seniority: 'VP',
    compensation: '$320k–$400k + equity',
    postedDays: 4,
    confidential: false,
    summary:
      'Build and lead the engineering organisation of a fast-moving AI infrastructure company.',
    responsibilities: [
      'Own engineering execution, quality and velocity',
      'Grow and develop a world-class engineering team',
      'Partner with product on a demanding roadmap',
      'Establish scalable engineering practices',
    ],
    requirements: [
      'Senior engineering leadership at a high-growth company',
      'Experience with infrastructure or ML systems',
      'Proven team-building track record',
      'Hands-on technical credibility',
    ],
  },
  {
    id: 'interim-cfo-brightline',
    title: 'Interim CFO',
    company: 'Brightline Retail',
    companyBlurb: 'A national retail group navigating a turnaround.',
    location: 'Manchester, United Kingdom',
    remote: 'On-site',
    type: 'Interim',
    discipline: 'Finance',
    seniority: 'C-Suite',
    compensation: '£1,400/day',
    postedDays: 9,
    confidential: false,
    summary:
      'Provide steady financial leadership through a critical transformation period.',
    responsibilities: [
      'Stabilise cash, controls and reporting',
      'Support the turnaround and refinancing',
      'Rebuild finance team confidence and rigour',
      'Prepare the ground for a permanent successor',
    ],
    requirements: [
      'Seasoned interim or portfolio CFO',
      'Turnaround and restructuring experience',
      'Retail or consumer background preferred',
      'Immediate availability',
    ],
  },
  {
    id: 'advisory-partner-orbit',
    title: 'Advisory Partner, Digital',
    company: 'Orbit Consulting',
    companyBlurb: 'A boutique strategy consultancy for the Fortune 500.',
    location: 'Remote (Global)',
    remote: 'Remote',
    type: 'Advisory',
    discipline: 'Technology',
    seniority: 'VP',
    compensation: 'Equity + fees',
    postedDays: 16,
    confidential: false,
    summary:
      'Advise leading enterprises on digital and AI transformation as part of a senior advisory bench.',
    responsibilities: [
      'Lead high-stakes client engagements',
      'Shape digital and AI transformation strategy',
      'Mentor consulting teams',
      'Grow the practice and client relationships',
    ],
    requirements: [
      'Partner or Principal-level consulting experience',
      'Deep digital / AI transformation expertise',
      'Trusted advisor to C-suite clients',
      'Strong commercial network',
    ],
  },
  {
    id: 'director-ops-cascade',
    title: 'Director of Operations',
    company: 'Cascade Manufacturing',
    companyBlurb: 'A precision manufacturing leader in aerospace.',
    location: 'Seattle, United States',
    remote: 'On-site',
    type: 'Full-time',
    discipline: 'Operations',
    seniority: 'Director',
    compensation: '$210k–$260k',
    postedDays: 7,
    confidential: false,
    summary:
      'Lead plant operations and continuous improvement for a precision aerospace manufacturer.',
    responsibilities: [
      'Own site operations, safety and quality',
      'Drive lean and continuous improvement',
      'Develop operational leaders',
      'Deliver on cost, quality and delivery targets',
    ],
    requirements: [
      'Operations leadership in advanced manufacturing',
      'Lean / Six Sigma expertise',
      'Aerospace or regulated industry experience',
      'Strong people leadership',
    ],
  },
]

export interface Discipline_Filter {
  label: Discipline
  count: number
}

export const disciplines: Discipline[] = [
  'Executive Leadership',
  'Finance',
  'Technology',
  'Marketing',
  'Operations',
  'People & Culture',
  'Legal',
]

export const jobTypes: JobType[] = [
  'Executive Search',
  'Full-time',
  'Board',
  'Interim',
  'Advisory',
]

export const remoteOptions = ['On-site', 'Hybrid', 'Remote'] as const

export const stats = [
  { value: '2,400+', label: 'Executive placements' },
  { value: '38', label: 'Countries served' },
  { value: '96%', label: 'Retention at two years' },
  { value: '$18B', label: 'Client value created' },
]

export const clientLogos = [
  'MERIDIAN',
  'NORTHWIND',
  'LUMEN',
  'VANTAGE',
  'ARBOR',
  'HELIOS',
  'TERRA',
  'ORBIT',
  'CASCADE',
  'NIMBUS',
]

export interface Service {
  title: string
  description: string
  points: string[]
}

export const services: Service[] = [
  {
    title: 'Executive Search',
    description:
      'Discreet, rigorous searches for CEO, C-suite and senior leadership mandates.',
    points: ['Bespoke research', 'Confidential process', 'Global reach'],
  },
  {
    title: 'Board & Advisory',
    description:
      'Chair, non-executive director and advisory appointments that shape governance.',
    points: ['Board composition', 'Succession planning', 'Governance review'],
  },
  {
    title: 'Leadership Assessment',
    description:
      'Objective evaluation of leaders and teams to de-risk your most important decisions.',
    points: ['Executive assessment', 'Team effectiveness', 'Onboarding'],
  },
  {
    title: 'Interim Leadership',
    description:
      'Experienced interim executives deployed at pace for transformation and transition.',
    points: ['Rapid deployment', 'Turnaround', 'Coverage'],
  },
]

export interface ProcessStep {
  number: string
  title: string
  description: string
}

export const processSteps: ProcessStep[] = [
  {
    number: '01',
    title: 'Understand',
    description:
      'We immerse ourselves in your business, culture and ambition to define the mandate with precision.',
  },
  {
    number: '02',
    title: 'Research',
    description:
      'Our global research team maps the market, identifying and discreetly approaching the finest talent.',
  },
  {
    number: '03',
    title: 'Assess',
    description:
      'Rigorous, structured assessment ensures every shortlisted leader is exceptional and the right fit.',
  },
  {
    number: '04',
    title: 'Place & Integrate',
    description:
      'We guide offer, transition and onboarding — then stay close to ensure lasting success.',
  },
]

export interface Testimonial {
  quote: string
  name: string
  role: string
  company: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'They understood our business better than any firm we have worked with. The CEO they placed has transformed our trajectory.',
    name: 'Eleanor Whitmore',
    role: 'Board Chair',
    company: 'Lumen Renewables',
  },
  {
    quote:
      'Discreet, rigorous and genuinely partner-led. This is executive search as it should be — a true strategic partnership.',
    name: 'Marcus Devlin',
    role: 'Managing Partner',
    company: 'Northwind Capital',
  },
  {
    quote:
      'From brief to onboarding, the process was flawless. The calibre of the shortlist was extraordinary.',
    name: 'Priya Nair',
    role: 'Chief Executive',
    company: 'Vantage Health',
  },
]

export interface Insight {
  id: string
  category: string
  title: string
  excerpt: string
  date: string
  readTime: string
  image: string
}

export const insights: Insight[] = [
  {
    id: 'succession',
    category: 'Leadership',
    title: 'The quiet art of executive succession',
    excerpt:
      'Why the best boards treat succession as a continuous discipline, not a moment of crisis.',
    date: 'June 2026',
    readTime: '6 min read',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0c633014-e5a4-4dfe-8b7d-c883115e6e3b.jpg',
  },
  {
    id: 'talent-markets',
    category: 'Market Insight',
    title: 'Where the world\u2019s leadership talent is moving',
    excerpt:
      'A data-led look at the cross-border flows reshaping the market for senior executives.',
    date: 'May 2026',
    readTime: '8 min read',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ec0f7740-210a-4fde-a2de-2e7216386c38.jpg',
  },
  {
    id: 'first-90-days',
    category: 'Onboarding',
    title: 'The first ninety days that define a tenure',
    excerpt:
      'What separates leaders who thrive from those who stall in the critical opening chapter.',
    date: 'May 2026',
    readTime: '5 min read',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b87dbfbb-5d9c-42c7-b99f-623664b45687.jpg',
  },
]

export const heroImage =
  'https://cdn.magicpatterns.com/patterns/generated-images/4edf86f0-5ddc-4107-b515-d96a52bb3173.jpg'
export const processImage =
  'https://cdn.magicpatterns.com/patterns/generated-images/3813d8fc-8637-45c8-8dc9-d4f9f3852386.jpg'

export function formatPosted(days: number): string {
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  const weeks = Math.round(days / 7)
  return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
}
